import React, { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { LoadingDots, NewSelect, colors, spacing } from '@retailer-platform/shared-components'
import { type DashStyleValue } from 'highcharts'
import { trackEvent } from 'utils/events/trackEvent'
import {
  InsightsPortalWidgetContainer,
  ViewByText,
} from 'domains/insights-portal/utils/insights-portal.styles'
import { InsightsPortalTitle } from 'domains/insights-portal/components/title/InsightsPortalTitle'
import { useDomainMessages } from 'domains/insights-portal/utils/domain/intl'
import { type KpiTrendLineProps } from 'domains/insights-portal/widgets/KPIOverviewTrend/utils/KpiOverviewTrend.types'
import { InsightsPortalTrendGraph } from './InsightsPortalTrendGraph'
import { InsightsPortalMetrics } from './InsightsPortalMetrics'

const KpiOverviewTrendGraphHeader = styled(InsightsPortalWidgetContainer)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${spacing.X4} 19px`,
})

const GroupByDateSelector = styled.div({
  display: 'flex',
})

const LoadingDotsContainer = styled.div({
  height: '400px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const dashType: DashStyleValue = 'Dash'

export const KpiTrendLine: FunctionComponent<KpiTrendLineProps> = ({
  analyticTrackingData,
  isLoading,
  setSelectedMetric,
  selectedMetric,
  listOfMetricItemsWithData,
  title,
  setSelectedGroupBy,
  selectedGroupBy,
  groupBys,
  isLoadingMultiGroupData,
  comparisonColor,
  comparisonSeriesName,
  primarySeriesName,
  primaryData,
  comparisonData,
  benchmarkData,
  selectedMetricName,
  selectedMetricFormatter,
  benchmarkColor,
  benchmarkSeriesName,
  showExportMenu,
  showAdditionalSeriesTrendLines,
  chartContainerStyles,
  sideComponent,
}) => {
  const messages = useDomainMessages({
    viewBy: 'insightsPortalDomain.controls.viewBy.label',
    benchmarkComparison: 'insightsPortalDomain.benchmarkComparison',
    currentPeriod: 'insightsPortalDomain.overallPerformance.currentPeriodLabel',
  })
  return (
    <>
      <InsightsPortalMetrics
        onClick={activeMetric => {
          trackEvent({
            id: `insights_portal.metric_card.changed`,
            description: `Selected a metric card`,
            data: {
              metric: activeMetric,
              widgetName: 'kpiOverview',
              widgetId: `kpi-overview`,
              dashboardName: analyticTrackingData?.dashboard,
            },
          })
          setSelectedMetric(activeMetric)
        }}
        isLoading={isLoading}
        selectedMetric={selectedMetric}
        metricInfoWithData={listOfMetricItemsWithData}
      />
      <div css={{ display: 'flex' }}>
        <div css={{ width: sideComponent ? '70%' : '100%' }}>
          <KpiOverviewTrendGraphHeader>
            <InsightsPortalTitle data-testid="kpi-overview-trend-title">
              {title}
            </InsightsPortalTitle>
            <GroupByDateSelector>
              <ViewByText>{messages.viewBy}:</ViewByText>
              <NewSelect
                styles={{
                  control: baseStyles => ({
                    ...baseStyles,
                    fontSize: '12px',
                  }),
                }}
                onChange={value => {
                  trackEvent({
                    id: `insights_portal.kpiOverview.view_by.changed`,
                    description: `Selected a view by from the dropdown in the overview widget`,
                    data: {
                      value,
                      widgetName: 'kpiOverview',
                      dashboardName: analyticTrackingData?.dashboard,
                    },
                  })
                  setSelectedGroupBy(value)
                }}
                defaultValue={selectedGroupBy}
                options={groupBys}
              />
            </GroupByDateSelector>
          </KpiOverviewTrendGraphHeader>
          <div>
            {isLoadingMultiGroupData ? (
              <LoadingDotsContainer>
                <LoadingDots />
              </LoadingDotsContainer>
            ) : (
              <InsightsPortalTrendGraph
                primaryColor={colors.HIGHLIGHT.DARK}
                comparisonColor={comparisonColor}
                comparisonSeriesName={comparisonSeriesName}
                primarySeriesName={primarySeriesName}
                primaryData={primaryData}
                comparisonData={comparisonData}
                benchmarkData={benchmarkData}
                metricName={selectedMetricName}
                metricFormatter={selectedMetricFormatter}
                showExportMenu={showExportMenu}
                analyticTrackingData={analyticTrackingData}
                showAdditionalSeriesTrendLines={showAdditionalSeriesTrendLines}
                additionalSeries={[
                  {
                    data: comparisonData,
                    color: comparisonColor,
                    seriesName: comparisonSeriesName,
                    dashType: 'Solid',
                  },
                  ...(benchmarkData.some(benchmarkDataItem => benchmarkDataItem.y !== undefined)
                    ? [
                        {
                          data: benchmarkData,
                          color: benchmarkColor,
                          dashType: dashType,
                          seriesName: benchmarkSeriesName,
                        },
                      ]
                    : []),
                ]}
                containerStyle={chartContainerStyles}
              />
            )}
          </div>
        </div>
        {sideComponent && sideComponent}
      </div>
    </>
  )
}
